/**
 * Dosya türünü kontrol eder
 * @param {File} file - Kontrol edilecek dosya
 * @returns {boolean} - Dosyanın görsel olup olmadığı
 */
export const isImageFile = (file) => {
  return file && file.type && file.type.startsWith('image/')
}

/**
 * Dosya boyutunu kontrol eder
 * @param {File} file - Kontrol edilecek dosya
 * @param {Number} maxSize - İzin verilen maksimum boyut
 * @returns {boolean} - Dosyanın boyut limitinin altında olup olmadığı
 */
export const isUnderSizeLimit = (file, maxSize) => {
  return file.size <= maxSize
}

/**
 * Görsel boyutlarını hesaplar
 * @param {number} width - Orijinal genişlik
 * @param {number} height - Orijinal yükseklik
 * @param {number} maxDimension - Maksimum boyut
 * @returns {Object} - Yeni genişlik ve yükseklik değerleri
 */
export const calculateDimensions = (width, height, maxDimension) => {
  if (width <= maxDimension && height <= maxDimension) {
    return { width, height }
  }

  if (width > height) {
    return {
      width: maxDimension,
      height: height * (maxDimension / width),
    }
  } else {
    return {
      width: width * (maxDimension / height),
      height: maxDimension,
    }
  }
}

/**
 * Canvas üzerinde görseli yeniden boyutlandırır
 * @param {HTMLImageElement} img - Görsel elementi
 * @param {number} width - Yeni genişlik
 * @param {number} height - Yeni yükseklik
 * @returns {HTMLCanvasElement} - Oluşturulan canvas
 */
export const resizeImageOnCanvas = (img, width, height) => {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  ctx.drawImage(img, 0, 0, width, height)
  return canvas
}

/**
 * Canvas'tan Blob oluşturur
 * @param {HTMLCanvasElement} canvas - Kaynak canvas
 * @param {string} type - Görsel türü
 * @param {number} quality - Sıkıştırma kalitesi
 * @returns {Promise<Blob>} - Oluşturulan blob
 */
export const canvasToBlob = (canvas, type, quality) => {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality)
  })
}

/**
 * Blob'dan File oluşturur
 * @param {Blob} blob - Kaynak blob
 * @param {string} fileName - Dosya adı
 * @param {string} fileType - Dosya türü
 * @returns {File} - Oluşturulan dosya
 */
export const createFileFromBlob = (blob, fileName, fileType) => {
  return new File([blob], fileName, { type: fileType })
}

/**
 * Görseli yüklemek için Promise döndürür
 * @param {string} src - Görsel kaynağı
 * @returns {Promise<HTMLImageElement>} - Yüklenen görsel
 */
export const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = (error) => reject(error)
    img.src = src
  })
}

/**
 * Aşamalı olarak görsel sıkıştırma dener
 * @param {HTMLCanvasElement} canvas - Kaynak canvas
 * @param {string} fileType - Dosya türü
 * @param {string} fileName - Dosya adı
 * @param {number} maxSize - Maksimum boyut
 * @returns {Promise<File>} - Optimize edilmiş dosya
 */
export const compressImage = async (canvas, fileType, fileName, maxSize) => {
  let quality = 0.9
  let attempt = 0
  let blob

  while (attempt < 5) {
    blob = await canvasToBlob(canvas, fileType, quality)

    if (blob.size <= maxSize) {
      break
    }

    attempt++
    quality -= 0.1
    quality = Math.max(quality, 0.5)
  }

  return createFileFromBlob(blob, fileName, fileType)
}

/**
 * Dosya boyutunu azaltmak için görselleri yeniden boyutlandırır ve optimize eder
 *
 * @param {File} file - Optimize edilecek görsel dosyası
 * @param {Number} maxSize - Bayt cinsinden maksimum dosya boyutu
 * @param {Number} maxDimension - Piksel cinsinden maksimum boyut (genişlik/yükseklik)
 * @returns {Promise<File>} - Optimize edilmiş görsel dosyası
 */
export const optimizeImage = async (file, maxSize = 5 * 1024 * 1024, maxDimension = 1600) => {
  try {
    // Dosya tipini kontrol et
    if (!isImageFile(file)) {
      return file
    }

    // Boyut kontrolü
    if (isUnderSizeLimit(file, maxSize)) {
      return file
    }

    // Dosyayı oku
    const dataUrl = await readFileAsDataURL(file)

    // Görseli yükle
    const img = await loadImage(dataUrl)

    // Boyutları hesapla
    const { width, height } = calculateDimensions(img.width, img.height, maxDimension)

    // Canvas'a çiz
    const canvas = resizeImageOnCanvas(img, width, height)

    // Sıkıştır
    return await compressImage(canvas, file.type, file.name, maxSize)
  } catch (error) {
    console.error('Görsel optimizasyonu sırasında hata:', error)
    return file // Hata durumunda orijinali döndür
  }
}

/**
 * Dosyayı DataURL'e dönüştürür
 * @param {File} file - Dönüştürülecek dosya
 * @returns {Promise<string>} - DataURL
 */
export const readFileAsDataURL = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => resolve(e.target.result)
    reader.onerror = (error) => reject(error)
    reader.readAsDataURL(file)
  })
}